import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Crown } from 'lucide-react';
import { getBackendURL } from '../../utils/backendURL';

function trialEnd(start, months) {
  const date = new Date(start);
  const day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + months);
  const lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  date.setUTCDate(Math.min(day, lastDay));
  return date;
}

export default function BusinessTrialControl({ user, disabled, onUpdated }) {
  const [details, setDetails] = useState({});
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState('');
  useEffect(() => {
    let mounted = true;
    setReady(false);
    setLoadError('');
    axios.get(`${getBackendURL()}/api/ops/users/${user.id}/business-trial`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
    }).then(({ data }) => { if (mounted) { setDetails(data); setReady(true); } })
      .catch(() => { if (mounted) setLoadError('Could not load trial limits. Reopen user details to retry.'); });
    return () => { mounted = false; };
  }, [user]);
  const account = { ...user, ...details };
  const editing = account.plan === 'business_trial';
  const teamLimitSource = { business_trial: 'Business Trial', commercial_agreement: 'Commercial agreement',
    plan: 'Plan' }[account.team_limit_source] || 'Plan';
  const configuredMonths = account.trial_duration_months ?? 3;
  const [executions, setExecutions] = useState('50');
  const [members, setMembers] = useState('5');
  const validLimits = /^\d+$/.test(executions) && Number(executions) >= 1 && Number(executions) <= 1000000 &&
    /^\d+$/.test(members) && Number(members) >= 1 && Number(members) <= 100000;
  const [open, setOpen] = useState(false);
  const [months, setMonths] = useState(String(user.trial_duration_months || 3));
  const [start, setStart] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const duration = Number(months);
  const valid = months.trim() !== '' && Number.isInteger(duration) && validLimits && duration >= 1 && duration <= 120;
  // The API stores UTC without an offset. Parse that representation explicitly as UTC.
  const parseStart = (value) => typeof value === 'string' && !/(Z|[+-]\d\d:\d\d)$/.test(value) ? value + 'Z' : value;
  const end = start && valid ? trialEnd(parseStart(start), duration) : null;
  const expired = end && end <= new Date();
  const dateLabel = end && !Number.isNaN(end.getTime()) ? end.toLocaleDateString() : '—';

  const showDialog = () => {
    setStart(editing ? (account.trial_start_date || account.billing_cycle_start || new Date().toISOString()) : new Date().toISOString());
    setMonths(String(account.trial_duration_months || 3));
    setExecutions(String(account.trial_executions_monthly_limit || 50));
    setMembers(String(account.trial_team_members_limit || 5));
    setError('');
    setOpen(true);
  };

  const confirm = async () => {
    if (!valid || expired || saving) return;
    setSaving(true);
    setError('');
    try {
      const response = await axios.put(`${getBackendURL()}/api/ops/users/${user.id}/business-trial`, {
        duration_months: duration, executions_monthly: Number(executions), team_members: Number(members),
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });
      setDetails({ ...account, plan: 'business_trial', trial_start_date: response.data.trial_start,
        trial_end_date: response.data.trial_end, trial_duration_months: duration,
        trial_executions_monthly_limit: Number(executions), trial_team_members_limit: Number(members) });
      await onUpdated({
        ...user, plan: 'business_trial', trial_start_date: response.data.trial_start,
        trial_end_date: response.data.trial_end, trial_duration_months: response.data.duration_months,
      });
      setOpen(false);
    } catch (failure) {
      const detail = failure.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not save the trial. Check the duration and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-4">
      <button type="button" onClick={showDialog} disabled={disabled || saving || !ready}
        className="w-full p-4 rounded-lg border-2 border-orange-200 bg-orange-50 disabled:opacity-50">
        <span className="flex items-center justify-center gap-2 font-semibold text-orange-700">
          <Crown className="w-5 h-5" />
          {editing ? 'Edit Business Trial' : 'Assign Business Trial'}
        </span>
        <span className="text-xs text-orange-600">Choose free months, executions and team members</span>
      </button>
      {loadError && <p role="alert" className="text-red-600 text-sm">{loadError}</p>}
      {ready && <p className="text-sm text-gray-600 mt-2">Effective team limit: {account.effective_team_members} ({teamLimitSource}). Commercial agreements are independent of this trial.</p>}
      {ready && editing && <p className="text-sm text-gray-600 mt-2">Executions per month: {account.trial_executions_monthly_limit}</p>}
      {ready && editing && account.trial_end_date && (
        <p className="text-sm text-gray-600 mt-2">Current trial ({configuredMonths} {configuredMonths === 1 ? 'month' : 'months'}) ends: {new Date(parseStart(account.trial_end_date)).toLocaleDateString()}</p>
      )}
      {open && (
        <div role="dialog" aria-modal="true" aria-labelledby="business-trial-title"
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 id="business-trial-title" className="text-lg font-semibold text-gray-900">
              {editing ? 'Edit Business Trial' : 'Assign Business Trial'}
            </h3>
            <p className="text-sm text-gray-600 my-3">Complimentary access for <strong>{user.email}</strong>.</p>
            <label htmlFor="business-trial-months" className="block text-sm font-medium">Free months</label>
            <input id="business-trial-months" type="number" min="1" max="120" step="1"
              value={months} onChange={(event) => setMonths(event.target.value)} disabled={saving}
              className="mt-1 w-full border rounded px-3 py-2" />
            <label className="block text-sm font-medium mt-3" htmlFor="trial-executions">Executions per month</label>
            <input id="trial-executions" type="number" min="1" max="1000000" step="1"
              value={executions} onChange={e => setExecutions(e.target.value)} disabled={saving} className="w-full border rounded px-3 py-2" />
            <label className="block text-sm font-medium mt-3" htmlFor="trial-members">Trial team members</label>
            <input id="trial-members" type="number" min="1" max="100000" step="1"
              value={members} onChange={e => setMembers(e.target.value)} disabled={saving} className="w-full border rounded px-3 py-2" />
            <p className="text-sm mt-2">Effective trial team limit: {account.custom_team_members_limit ?? members}
              {account.custom_team_members_limit != null ? ' (commercial agreement takes priority)' : ' (trial configuration)'}.</p>
            <p className="text-sm text-gray-600 mt-2">
              {editing ? 'Total calendar months from the original trial start. Editing preserves current usage.' : 'Calendar months from assignment. No payment is collected by this action.'}
            </p>
            <p className="text-sm mt-3">Trial ends: <strong>{dateLabel}</strong></p>
            <p className="text-sm text-gray-600 mt-2">Trial limits apply only until expiration. Commercial team agreements remain in effect.</p>
            {(!valid || expired) && <p role="alert" className="text-sm text-red-600 mt-2">
              {expired ? 'Choose a duration that ends in the future.' : 'Enter whole numbers: 1–120 months, 1–1,000,000 executions and 1–100,000 members.'}
            </p>}
            {error && <p role="alert" className="text-sm text-red-600 mt-2">{error}</p>}
            <div className="flex gap-3 mt-6">
              <button type="button" disabled={saving} onClick={() => setOpen(false)}
                className="flex-1 px-4 py-2 border rounded">Cancel</button>
              <button type="button" disabled={saving || !valid || expired} onClick={confirm}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50">
                {saving ? 'Saving...' : editing ? 'Save Trial' : 'Assign Trial'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
